import { useRef, useState } from "react";
import { useT } from "../../i18n/index.js";
import { compressImageToBase64 } from "../../lib/image-compress.js";

/**
 * Documentos da recepção — rapport de expertise, carte grise, CNH.
 *
 * Apresentacional: o gerente do chantier e o técnico SÊNIOR sobem os mesmos
 * documentos por mutations diferentes (cada portal tem o seu endpoint), então
 * a tela mora aqui e cada página liga a sua. Duas cópias da tela já tinham
 * produzido dois comportamentos diferentes para o mesmo botão.
 */

export type DocCat = "expertise" | "carte_grise" | "cnh";

export type DocRow = {
  id: number;
  category: string | null;
  fileName: string;
  sizeBytes: number | string | null;
};

export function ReceptionDocsCard({
  docs,
  sending,
  error,
  onPick,
  /** F-expertise-check — destaca em âmbar o que ainda falta para finalizar. */
  requiredCats = [],
}: {
  docs: DocRow[];
  sending: DocCat | null;
  error: string | null;
  onPick: (cat: DocCat, fileName: string, contentBase64: string) => void;
  requiredCats?: DocCat[];
}) {
  const t = useT();
  const CATS: Array<[DocCat, string]> = [
    ["expertise", t("chantier.docExpertise")],
    ["carte_grise", t("chantier.docCarteGrise")],
    ["cnh", t("chantier.docCnh")],
  ];
  const [erroLocal, setErroLocal] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const catRef = useRef<DocCat>("expertise");

  async function escolheu(file: File) {
    setErroLocal(null);
    try {
      const dataUrl = file.type.startsWith("image/")
        ? await compressImageToBase64(file, 1600, 0.8)
        : await new Promise<string>((ok, falhou) => {
            const fr = new FileReader();
            fr.onload = () => ok(String(fr.result));
            fr.onerror = () => falhou(new Error("read"));
            fr.readAsDataURL(file);
          });
      onPick(catRef.current, file.name, dataUrl.split(",")[1] ?? "");
    } catch {
      setErroLocal(t("chantier.docUploadFail"));
    }
  }

  return (
    <div className="rounded-lg border border-neutral-800 bg-neutral-950/60 p-3 space-y-2">
      <p className="text-[11px] uppercase tracking-wider text-neutral-500">
        📎 {t("chantier.docsTitle")}
      </p>
      {(error ?? erroLocal) && <p className="text-xs text-red-300">{error ?? erroLocal}</p>}
      <div className="space-y-1.5">
        {CATS.map(([cat, label]) => {
          const doc = docs.find((r) => r.category === cat);
          const falta = !doc && requiredCats.includes(cat);
          return (
            <div key={cat} className="flex items-center justify-between gap-2 text-xs">
              <span className="min-w-0">
                <span
                  className={
                    doc ? "text-neutral-400" : falta ? "text-amber-200" : "text-neutral-200"
                  }
                >
                  {falta && "⚠ "}
                  {label}
                </span>
                {doc && (
                  <span className="block truncate text-[10px] text-neutral-600">
                    {doc.fileName}
                    {doc.sizeBytes != null
                      ? ` · ${Math.round(Number(doc.sizeBytes) / 1024)} KB`
                      : ""}
                  </span>
                )}
              </span>
              <span className="flex shrink-0 items-center gap-1.5">
                {doc && (
                  <span className="rounded bg-lime/15 px-1.5 py-0.5 text-[10px] text-lime">
                    ✓ {t("chantier.docSentBadge")}
                  </span>
                )}
                <button
                  type="button"
                  disabled={sending === cat}
                  onClick={() => {
                    catRef.current = cat;
                    inputRef.current?.click();
                  }}
                  className={`rounded-md border px-2.5 py-1 text-[11px] disabled:opacity-50 ${
                    falta
                      ? "border-amber-500/60 text-amber-200 hover:border-amber-400"
                      : "border-neutral-700 text-neutral-400 hover:border-lime hover:text-lime"
                  }`}
                >
                  {sending === cat
                    ? t("chantier.docUploading")
                    : doc
                      ? t("chantier.docReplaceBtn")
                      : `+ ${label}`}
                </button>
              </span>
            </div>
          );
        })}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (f) void escolheu(f);
        }}
      />
    </div>
  );
}
